import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/shared/api/api-error';

import {
  impactMessage,
  useCreatePurchaseLimit,
  usePurchaseLimitImpact,
  useUpdatePurchaseLimit,
} from '../api/purchase-limits.queries';
import {
  purchaseLimitSchema,
  type PurchaseLimitFormValues,
} from '../schemas/purchase-limit.schema';
import type { PurchaseLimit } from '../types/purchase-limit.types';

const FIELDS = ['metric', 'maxAmount', 'periodDays', 'reason'] as const;
const REASON_CLASS =
  'border-input bg-background focus-visible:ring-ring w-full rounded-md border px-2 py-1.5 text-xs focus-visible:ring-2 focus-visible:outline-none';

type Target =
  | { kind: 'country'; countryId: number; countryName: string }
  | { kind: 'customer'; customerId: number; customerName: string };

/**
 * Create a limit for every customer of a country or for one customer, or edit
 * an existing one (amount and period; the kind is fixed once created). A value
 * limit is always in the country's settlement currency. Checkout applies it
 * before the customer pays; after saving, staff are told whom it already holds back.
 */
export function PurchaseLimitDialog({
  target,
  limit,
  onClose,
}: {
  target: Target;
  limit?: PurchaseLimit;
  onClose: () => void;
}) {
  const create = useCreatePurchaseLimit();
  const update = useUpdatePurchaseLimit();
  const mutation = limit ? update : create;
  const form = useForm<PurchaseLimitFormValues>({
    resolver: zodResolver(purchaseLimitSchema),
    defaultValues: {
      metric: limit?.metric ?? 'OrderValue',
      maxAmount: limit?.maxAmount ?? 0,
      periodDays: limit?.periodDays ?? 30,
      reason: '',
    },
  });
  const metric = useWatch({ control: form.control, name: 'metric' });
  const maxAmount = useWatch({ control: form.control, name: 'maxAmount' });
  const preview = usePurchaseLimitImpact();

  const { error } = mutation;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = FIELDS.find((name) => name.toLowerCase() === field.toLowerCase());
      if (key) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);
  const formError = error instanceof ApiError && !error.fieldErrors ? error.message : null;

  const audience =
    target.kind === 'country' ? `every customer in ${target.countryName}` : target.customerName;

  const audienceInput = () =>
    limit
      ? limit.customerId !== null
        ? { customerId: limit.customerId }
        : { countryId: limit.countryId }
      : target.kind === 'country'
        ? { countryId: target.countryId }
        : { customerId: target.customerId };

  const checkImpact = () => {
    const values = form.getValues();
    preview.mutate({
      ...audienceInput(),
      metric: values.metric,
      maxAmount: values.maxAmount,
      periodDays: values.periodDays,
      reason: 'impact preview',
    });
  };

  const submit = (values: PurchaseLimitFormValues) => {
    const reason = values.reason.trim();
    if (limit) {
      update.mutate(
        { id: limit.id, maxAmount: values.maxAmount, periodDays: values.periodDays, reason },
        { onSuccess: onClose },
      );
      return;
    }
    create.mutate(
      {
        ...audienceInput(),
        metric: values.metric,
        maxAmount: values.maxAmount,
        periodDays: values.periodDays,
        reason,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">
            {limit ? 'Edit purchase limit' : 'New purchase limit'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            For {audience}. Checked on every checkout before the customer pays; a customer&apos;s
            own limit replaces the country&apos;s limit of the same kind.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate className="space-y-4 text-xs" onSubmit={form.handleSubmit(submit)}>
            <FormField
              control={form.control}
              name="metric"
              render={({ field }) => (
                <FormItem>
                  <fieldset className="space-y-1.5" disabled={Boolean(limit)}>
                    <legend className="mb-1 font-medium">Limit on</legend>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="limit-metric"
                        checked={field.value === 'OrderValue'}
                        onChange={() => field.onChange('OrderValue')}
                      />
                      Amount spent (in the country&apos;s settlement currency)
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="limit-metric"
                        checked={field.value === 'OrderCount'}
                        onChange={() => field.onChange('OrderCount')}
                      />
                      Number of orders
                    </label>
                  </fieldset>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="maxAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {metric === 'OrderValue' ? 'Maximum amount' : 'Maximum orders'}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step={metric === 'OrderValue' ? '0.01' : '1'}
                        value={Number.isFinite(field.value) && field.value !== 0 ? field.value : ''}
                        onChange={(event) => field.onChange(Number(event.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="periodDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Period (days)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={366}
                        value={Number.isFinite(field.value) ? field.value : ''}
                        onChange={(event) => field.onChange(Number(event.target.value))}
                      />
                    </FormControl>
                    <FormDescription>Rolling window up to today.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason</FormLabel>
                  <FormControl>
                    <textarea {...field} rows={2} maxLength={500} className={REASON_CLASS} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {preview.data && (
              <p role="status" className="text-muted-foreground">
                {impactMessage({ id: 0, impact: preview.data }) ??
                  'Nobody is above this limit today.'}
              </p>
            )}
            {formError && (
              <p role="alert" className="text-destructive">
                {formError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={checkImpact}
                disabled={preview.isPending || !(maxAmount > 0)}
              >
                Check who it affects
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                Save limit
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
